import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  MenuItem,
  Paper,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
} from '@mui/material';
import { useCreatePlan, usePlans, useSyncPlan, useUpdatePlan } from '../hooks/api/usePlans';

const emptyForm = {
  slug: '',
  name: '',
  priceCentsBase: 0,
  billingCycle: 'monthly',
  includedSeats: 3,
  trialDays: 14,
  isActive: true,
  isPublic: false,
};

function formatPrice(cents, currency) {
  const value = (cents ?? 0) / 100;
  return new Intl.NumberFormat('pt-BR', { style: 'currency', currency: currency || 'BRL' }).format(value);
}

export function PlansPage() {
  const { t } = useTranslation();
  const { data: plans, isLoading, error } = usePlans();
  const createPlan = useCreatePlan();
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingPlan, setEditingPlan] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [syncMessage, setSyncMessage] = useState('');

  const updatePlan = useUpdatePlan(editingPlan?.uuid ?? '');
  const syncPlan = useSyncPlan(editingPlan?.uuid ?? '');

  const sortedPlans = useMemo(() => {
    if (!Array.isArray(plans)) return [];
    return [...plans].sort((a, b) => a.slug.localeCompare(b.slug));
  }, [plans]);

  const openCreate = () => {
    setEditingPlan(null);
    setForm(emptyForm);
    setSyncMessage('');
    setDialogOpen(true);
  };

  const openEdit = (plan) => {
    setEditingPlan(plan);
    setForm({
      slug: plan.slug,
      name: plan.name,
      priceCentsBase: plan.priceCentsBase,
      billingCycle: plan.billingCycle,
      includedSeats: plan.includedSeats,
      trialDays: plan.trialDays,
      isActive: plan.isActive,
      isPublic: plan.isPublic,
    });
    setSyncMessage('');
    setDialogOpen(true);
  };

  const closeDialog = () => {
    if (!createPlan.isPending && !updatePlan.isPending) {
      setDialogOpen(false);
    }
  };

  const handleSave = async () => {
    try {
      if (editingPlan) {
        await updatePlan.mutateAsync({
          name: form.name,
          priceCentsBase: Number(form.priceCentsBase),
          billingCycle: form.billingCycle,
          includedSeats: Number(form.includedSeats),
          trialDays: Number(form.trialDays),
          isActive: form.isActive,
          isPublic: form.isPublic,
        });
      } else {
        await createPlan.mutateAsync({
          slug: form.slug.trim().toLowerCase(),
          name: form.name.trim(),
          priceCentsBase: Number(form.priceCentsBase),
          billingCycle: form.billingCycle,
          includedSeats: Number(form.includedSeats),
          trialDays: Number(form.trialDays),
          isActive: form.isActive,
          isPublic: form.isPublic,
        });
      }
      setDialogOpen(false);
    } catch {
      // mutation error surfaces via isError if needed
    }
  };

  const handleSync = async () => {
    if (!editingPlan) return;
    const result = await syncPlan.mutateAsync();
    setSyncMessage(result?.message ?? t('plans.syncPending'));
  };

  return (
    <Box>
      <Box
        sx={{
          display: 'flex',
          flexDirection: { xs: 'column', sm: 'row' },
          justifyContent: 'space-between',
          alignItems: { xs: 'stretch', sm: 'center' },
          gap: 2,
          mb: 2,
        }}
      >
        <Typography variant="h5">{t('plans.title')}</Typography>
        <Button variant="contained" onClick={openCreate} sx={{ alignSelf: { xs: 'stretch', sm: 'auto' } }}>
          {t('plans.create')}
        </Button>
      </Box>

      {isLoading && (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}

      {error && <Alert severity="error">{error.message || t('common.error')}</Alert>}

      {!isLoading && !error && (
        <Paper>
          {sortedPlans.length === 0 ? (
            <Box sx={{ p: 3 }}>
              <Typography color="text.secondary">{t('plans.empty')}</Typography>
            </Box>
          ) : (
            <TableContainer sx={{ overflowX: 'auto' }}>
              <Table size="small" sx={{ minWidth: 640 }}>
                <TableHead>
                  <TableRow>
                    <TableCell>{t('plans.slug')}</TableCell>
                    <TableCell>{t('plans.name')}</TableCell>
                    <TableCell>{t('plans.price')}</TableCell>
                    <TableCell>{t('plans.cycle')}</TableCell>
                    <TableCell>{t('plans.includedSeats')}</TableCell>
                    <TableCell align="right"> </TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {sortedPlans.map((plan) => (
                    <TableRow key={plan.uuid} hover>
                      <TableCell>
                        <Typography variant="body2" fontFamily="monospace">
                          {plan.slug}
                        </Typography>
                      </TableCell>
                      <TableCell>{plan.name}</TableCell>
                      <TableCell>{formatPrice(plan.priceCentsBase, plan.currency)}</TableCell>
                      <TableCell>{t(`plans.cycle.${plan.billingCycle}`)}</TableCell>
                      <TableCell>{plan.includedSeats}</TableCell>
                      <TableCell align="right">
                        <Chip size="small" label={plan.isActive ? t('plans.isActive') : '—'} sx={{ mr: 1 }} />
                        <Button size="small" onClick={() => openEdit(plan)}>
                          {t('plans.edit')}
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </Paper>
      )}

      <Dialog open={dialogOpen} onClose={closeDialog} fullWidth maxWidth="sm">
        <DialogTitle>{editingPlan ? t('plans.edit') : t('plans.create')}</DialogTitle>
        <DialogContent sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 1 }}>
          {!editingPlan && (
            <TextField
              label={t('plans.slug')}
              value={form.slug}
              onChange={(e) => setForm((prev) => ({ ...prev, slug: e.target.value }))}
              required
              fullWidth
            />
          )}
          <TextField
            label={t('plans.name')}
            value={form.name}
            onChange={(e) => setForm((prev) => ({ ...prev, name: e.target.value }))}
            required
            fullWidth
          />
          <TextField
            label={t('plans.price')}
            type="number"
            value={form.priceCentsBase}
            onChange={(e) => setForm((prev) => ({ ...prev, priceCentsBase: e.target.value }))}
            fullWidth
          />
          <TextField
            select
            label={t('plans.cycle')}
            value={form.billingCycle}
            onChange={(e) => setForm((prev) => ({ ...prev, billingCycle: e.target.value }))}
            fullWidth
          >
            <MenuItem value="monthly">{t('plans.cycle.monthly')}</MenuItem>
            <MenuItem value="annual">{t('plans.cycle.annual')}</MenuItem>
          </TextField>
          <TextField
            label={t('plans.includedSeats')}
            type="number"
            value={form.includedSeats}
            onChange={(e) => setForm((prev) => ({ ...prev, includedSeats: e.target.value }))}
            fullWidth
          />
          <TextField
            label={t('plans.trialDays')}
            type="number"
            value={form.trialDays}
            onChange={(e) => setForm((prev) => ({ ...prev, trialDays: e.target.value }))}
            fullWidth
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.isActive}
                onChange={(e) => setForm((prev) => ({ ...prev, isActive: e.target.checked }))}
              />
            }
            label={t('plans.isActive')}
          />
          <FormControlLabel
            control={
              <Switch
                checked={form.isPublic}
                onChange={(e) => setForm((prev) => ({ ...prev, isPublic: e.target.checked }))}
              />
            }
            label={t('plans.isPublic')}
          />
          {syncMessage && (
            <Alert severity="info" onClose={() => setSyncMessage('')}>
              {syncMessage}
            </Alert>
          )}
        </DialogContent>
        <DialogActions>
          {editingPlan && (
            <Button onClick={handleSync} disabled={syncPlan.isPending}>
              {t('plans.sync')}
            </Button>
          )}
          <Button onClick={closeDialog}>{t('plans.cancel')}</Button>
          <Button variant="contained" onClick={handleSave} disabled={createPlan.isPending || updatePlan.isPending}>
            {t('plans.save')}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
